import React from 'react';
import { Await, useLoaderData } from 'react-router-dom';

import { DataTable } from '@oysterjs/ui/data-table';
import { SearchFilterInput, useFiltersFromLocalStorage } from '@oysterjs/ui/search-filter';

import {
  Action,
  HtmlDocumentTemplateFieldsFragment,
  Resource,
  VisibilityConfigInput
} from '~/api/graphql';
import { usePermissions } from '~/components/permissions';
import { BulkVisibilityDialog } from '~/components/visibility/bulk-visibility-dialog';
import { useVisibilityEntitlement } from '~/components/visibility/use-visibility-entitlement';

import { useCurrentViewer } from '~/components/library/hooks/use-current-viewer';
import { useFloatingSelection } from '~/components/list/use-floating-selection-bar';
import { useLibraryBulkArchive } from '~/components/list/use-library-bulk-archive';
import { useLibraryBulkVisibility } from '~/components/library/hooks/use-library-bulk-visibility';

import { ArchiveConfirmDialog } from '~/components/list/archive-confirm-dialog';
import { FeatureDocCallout } from './feature-doc-callout';
import { FloatingSelectionBar } from '~/components/list/floating-selection-bar';
import { LibraryListEmptyState } from '~/components/list/library-list-empty-state';
import { LibraryListLoading } from '~/components/list/library-list-loading';
import { LibraryListOutlet } from './library-list-outlet';
import {
  createStyleFilterDefinitions,
  filterStyleTemplates,
  type StyleFilter
} from '~/api/data/html-templates/styles-filters';
import { getStyleColumns, styleSortFn, type StyleSortKey } from './styles-columns';

export type HtmlTemplateListKind = 'styles' | 'templates';

/** Editing a row, archiving it, and changing its visibility are all updates. */
const UPDATE_TEMPLATE = [{ resource: Resource.HtmlDocumentTemplate, action: Action.Update }];

const KIND_CONFIG: Record<
  HtmlTemplateListKind,
  {
    listUrl: string;
    archivePath: string;
    visibilityPath: string;
    pluralNoun: string;
    singularNoun: string;
    emptyTitle: string;
    emptyDescription: string;
    calloutTitle: string;
    calloutDescription: string;
    filterStoreKey: string;
    /** Filter the raw template list down to this kind's rows. */
    filterByKind: (template: HtmlDocumentTemplateFieldsFragment) => boolean;
  }
> = {
  styles: {
    listUrl: '/app/library/styles',
    archivePath: '/app/library/styles/archive',
    visibilityPath: '/app/library/styles/visibility',
    pluralNoun: 'styles',
    singularNoun: 'style',
    emptyTitle: 'No styles found',
    emptyDescription: 'No styles available.',
    calloutTitle: 'Document styles',
    calloutDescription:
      'Create reusable static features for documents (cover pages, typography, headers/footers). Cara uses them when drafting long-form documents in a workspace. These can be used independently of templates (ie dynamic body content). Independent styles cannot override a template’s specific style.',
    filterStoreKey: 'cara_styles_default_view',
    filterByKind: (t) => !t.bodyTemplate || t.bodyTemplate.trim() === ''
  },
  templates: {
    listUrl: '/app/library/templates',
    archivePath: '/app/library/templates/archive',
    visibilityPath: '/app/library/templates/visibility',
    pluralNoun: 'templates',
    singularNoun: 'template',
    emptyTitle: 'No templates found',
    emptyDescription: 'No templates available.',
    calloutTitle: 'Document templates',
    calloutDescription:
      'Templates pair a saved style with a reusable body. Cara uses them when drafting structured documents that share repeated content.',
    filterStoreKey: 'cara_templates_default_view',
    filterByKind: (t) => !!t.bodyTemplate && t.bodyTemplate.trim() !== ''
  }
};

/**
 * Shared list-page logic for HTML styles and HTML templates. They differ in
 * which rows they include (bodyTemplate empty vs non-empty), the callout
 * copy, and the column variant — everything else (filter store, sort,
 * selection, archive, visibility) is identical.
 *
 * Each list-page entrypoint (`library/styles.tsx`, `library/templates.tsx`)
 * renders this component with its `kind`. Child routes for `new`, `:id`,
 * `archive`, `visibility` are owned by the per-kind page; this component
 * renders the corresponding outlet so the edit slideouts overlay the list.
 */
export function HtmlTemplateListPage(props: {
  kind: HtmlTemplateListKind;
  /**
   * Optional primary-action button rendered inline at the right of the
   * filter bar. Buttons should use `min-h-12` so they line up with the
   * search bar's minimum height.
   *
   * The list is read-only without update permission, so a create button passed
   * here should declare its own `permissions` to escape that.
   */
  extraActions?: React.ReactNode;
  /** Replaces the kind's callout description. */
  calloutDescription?: React.ReactNode;
}) {
  const config = KIND_CONFIG[props.kind];

  const loader = useLoaderData() as {
    data?: Promise<{ htmlDocumentTemplates?: HtmlDocumentTemplateFieldsFragment[] }>;
  };

  const viewer = useCurrentViewer();
  const visibilityEntitled = useVisibilityEntitlement();
  const canUpdate = usePermissions().hasAllPermissions(UPDATE_TEMPLATE);

  const filterDefinitions = React.useMemo(() => createStyleFilterDefinitions(), []);
  const { activeFilters, setActiveFilters, initialFilters, sortBy, sortDirection, setSort } =
    useFiltersFromLocalStorage<StyleFilter, StyleSortKey>(
      config.filterStoreKey,
      filterDefinitions,
      { defaultSortDirection: 'desc' }
    );

  const { selected, setSelected, selectedSet, clear } = useFloatingSelection();

  const archive = useLibraryBulkArchive({
    actionPath: config.archivePath,
    onArchived: clear
  });

  const visibility = useLibraryBulkVisibility({
    actionPath: config.visibilityPath,
    itemPlural: config.pluralNoun,
    onUpdated: clear
  });

  return (
    <div className="space-y-4">
      <FeatureDocCallout
        variant="sky"
        title={config.calloutTitle}
        description={props.calloutDescription ?? config.calloutDescription}
        docHref="https://docs.getcara.ai"
      />
      <div className="flex flex-wrap items-start gap-4">
        <div className="min-w-0 flex-1">
          <SearchFilterInput
            filterDefinitions={filterDefinitions}
            onFiltersChange={setActiveFilters}
            initialFilters={initialFilters}
            placeholder="Filter by title, creator, or search..."
          />
        </div>
        {props.extraActions}
      </div>
      <React.Suspense fallback={<LibraryListLoading title={`Loading ${config.pluralNoun}...`} />}>
        <Await resolve={loader?.data}>
          {(data: { htmlDocumentTemplates?: HtmlDocumentTemplateFieldsFragment[] } | undefined) => {
            const all = data?.htmlDocumentTemplates ?? [];
            const visible = filterStyleTemplates(
              all.filter(config.filterByKind),
              activeFilters,
              viewer
            );

            return (
              <div className="relative">
                {canUpdate && (
                  <FloatingSelectionBar
                    count={selected.length}
                    onClear={clear}
                    onVisibility={() => visibility.openDialog(selected)}
                    onArchive={archive.openDialog}
                    isArchiving={archive.isArchiving}
                    isUpdatingVisibility={visibility.isUpdating}
                    visibilityEnabled={visibilityEntitled}
                  />
                )}
                {visible.length === 0 ? (
                  <LibraryListEmptyState
                    title={config.emptyTitle}
                    description={
                      activeFilters.length > 0
                        ? 'Try adjusting your search or filters.'
                        : config.emptyDescription
                    }
                  />
                ) : (
                  <div className="px-4 sm:px-6 rounded-lg bg-white shadow dark:bg-neutral-800">
                    <DataTable
                      data={visible}
                      columns={getStyleColumns({
                        selectedSet,
                        setSelectedTemplates: setSelected,
                        filtered: visible,
                        editBase: config.listUrl,
                        pluralLabel: config.pluralNoun,
                        canEdit: canUpdate
                      })}
                      keyExtractor={(template) => template.id}
                      rowHref={(template) => `${config.listUrl}/${template.id}`}
                      tableClassName="[--gutter:theme(spacing.4)] sm:[--gutter:theme(spacing.6)]"
                      sortKey={sortBy}
                      sortDirection={sortDirection}
                      onSort={setSort}
                      sortFn={styleSortFn}
                    />
                  </div>
                )}
              </div>
            );
          }}
        </Await>
      </React.Suspense>

      <ArchiveConfirmDialog
        open={archive.dialogOpen}
        count={selected.length}
        itemNoun={config.singularNoun}
        itemPlural={config.pluralNoun}
        loading={archive.isArchiving}
        onCancel={archive.closeDialog}
        onConfirm={() => archive.archive(selected)}
      />

      <BulkVisibilityDialog
        open={visibility.open}
        count={visibility.ids.length}
        itemLabel={config.singularNoun}
        notice="This changes only the selected templates. Existing documents and workspaces keep their current visibility."
        resultMessage={visibility.resultMessage}
        loading={visibility.isUpdating}
        onClose={visibility.closeDialog}
        onSave={(v: VisibilityConfigInput) => visibility.save(v)}
      />

      <LibraryListOutlet />
    </div>
  );
}
